<script setup lang="ts">
import {
  type CalendarDate,
  getLocalTimeZone,
  today,
  type DateValue,
} from '@internationalized/date';
import { Calendar as CalendarIcon } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useDateRangeStore } from '@/stores/date-range';
import { storeToRefs } from 'pinia';

const dateRangeStore = useDateRangeStore();
const { selectedDateRange } = storeToRefs(dateRangeStore);

// Calendar picker state
const isCalendarOpen = ref(false);

// Get the selected day (since start and end are the same for single day)
const selectedDay = computed(() => selectedDateRange.value.start);

// Format the selected date
const formattedDate = computed(() => {
  const date = selectedDay.value;
  const jsDate = new Date(date.year, date.month - 1, date.day);
  return jsDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
});

// Handle day selection
function onDaySelect(value: DateValue | undefined) {
  if (!value) return;

  // Set both start and end to the same day
  dateRangeStore.setDateRange(value as CalendarDate, value as CalendarDate);
  isCalendarOpen.value = false;
}

// Quick select options
function selectToday() {
  const todayValue = today(getLocalTimeZone());
  dateRangeStore.setDateRange(todayValue, todayValue);
  isCalendarOpen.value = false;
}

// Get today's date
const todayDate = computed(() => today(getLocalTimeZone()));
</script>

<template>
  <Popover v-model:open="isCalendarOpen">
    <PopoverTrigger as-child>
      <Button variant="outline" class="justify-start text-left font-normal h-9 w-auto">
        <CalendarIcon class="mr-2 h-4 w-4" />
        <span>{{ formattedDate }}</span>
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-auto p-0" align="start">
      <div class="p-3 border-b">
        <div class="flex items-center justify-between mb-3">
          <h4 class="font-semibold text-sm">Select Day</h4>
          <Button variant="ghost" size="sm" class="h-7 text-xs" @click="selectToday">
            Today
          </Button>
        </div>
      </div>

      <!-- Single Day Calendar -->
      <div class="p-0">
        <Calendar
          :model-value="selectedDay"
          :max-value="todayDate"
          @update:model-value="onDaySelect"
        />
      </div>

      <div class="p-3 border-t text-xs text-muted-foreground text-center">
        Select a single day to view staff activity
      </div>
    </PopoverContent>
  </Popover>
</template>
